import React, { useState, useEffect } from 'react';
import {
  useListContext,
  useNotify,
  useRefresh,
  useUnselectAll,
  Button,
} from 'react-admin';
import SendIcon from '@mui/icons-material/Send';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { supabaseClient } from './supabase';

// Helper to fetch all records with pagination (Supabase has 1000 row limit)
const fetchAllParents = async () => {
  const pageSize = 1000;
  let allParents = [];
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabaseClient
      .from('parents')
      .select('*')
      .order('last_name', { ascending: true })
      .range(from, to);

    if (error) throw error;

    if (data && data.length > 0) {
      allParents = allParents.concat(data);
      page++;
      if (data.length < pageSize) hasMore = false;
    } else {
      hasMore = false;
    }
  }

  return allParents;
};

const BulkInviteButton = () => {
  const {
    selectedIds,
    data,
    total,
    onSelect,
    filterValues,
  } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll('parents');
  const [loading, setLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [allFilteredData, setAllFilteredData] = useState(null);

  // Reset allFilteredData when selection is cleared or filters change
  useEffect(() => {
    if (selectedIds.length === 0) {
      setAllFilteredData(null);
    }
  }, [selectedIds.length]);

  // Also reset when filters change
  useEffect(() => {
    setAllFilteredData(null);
  }, [filterValues?.grade, filterValues?.status]);

  // Check if all items on current page are selected
  const allOnPageSelected = data && selectedIds.length === data.length && data.length > 0;
  const hasMoreRecords = total > selectedIds.length;
  const allRecordsSelected = allFilteredData && selectedIds.length === allFilteredData.length;

  const handleSelectAll = async () => {
    setSelectingAll(true);
    try {
      // Fetch all parents using pagination
      let allParents = await fetchAllParents();

      // Enrich with auth data
      const { data: users } = await supabaseClient.functions.invoke('getAuthUsers');
      const userMap = users ? Object.fromEntries(users.map((u) => [u.email, u])) : {};

      allParents = allParents.map((p) => ({
        ...p,
        invited_at: userMap[p.email]?.invited_at ?? null,
        last_sign_in_at: userMap[p.email]?.last_sign_in_at ?? null,
        authid: userMap[p.email]?.id ?? null,
      }));

      // Apply grade filter if set
      if (filterValues?.grade) {
        const { data: students } = await supabaseClient
          .from('students')
          .select('household_hash')
          .eq('grade', filterValues.grade);

        if (students) {
          const householdsWithGrade = new Set(students.map((s) => s.household_hash));
          allParents = allParents.filter((p) => householdsWithGrade.has(p.household_hash));
        }
      }

      // Apply status filter if set
      if (filterValues?.status === 'active') {
        allParents = allParents.filter((p) => p.last_sign_in_at !== null);
      } else if (filterValues?.status === 'inactive') {
        allParents = allParents.filter((p) => p.last_sign_in_at === null);
      }

      // Select all IDs
      const allIds = allParents.map((p) => p.id);
      onSelect(allIds);
      setAllFilteredData(allParents);

      notify(`${allIds.length} ouers geselekteer`, { type: 'info' });
    } catch (err) {
      console.error('Error selecting all:', err);
      notify('Kon nie alle ouers selekteer nie', { type: 'error' });
    } finally {
      setSelectingAll(false);
    }
  };

  const handleBulkInvite = async () => {
    if (selectedIds.length === 0) {
      notify('Geen ouers geselekteer nie', { type: 'warning' });
      return;
    }

    // Get the parent records - either from allFilteredData or from visible data
    let selectedParents;
    if (allFilteredData) {
      selectedParents = allFilteredData.filter((p) => selectedIds.includes(p.id));
    } else {
      selectedParents = selectedIds
        .map((id) => data.find((parent) => parent.id === id))
        .filter(Boolean);
    }

    if (selectedParents.length === 0) {
      notify('Kon nie geselekteerde ouer-rekords vind nie', { type: 'error' });
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: selectedParents.length });

    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < selectedParents.length; i++) {
      const parent = selectedParents[i];
      setProgress({ current: i + 1, total: selectedParents.length });

      try {
        const response = await supabaseClient.functions.invoke('resetAuthUser', {
          body: {
            id: parent.authid || null,
            email: parent.email,
          },
        });

        // The function returns 201 (invite) or 205 (reset) with potentially empty body
        // functions.invoke may throw JSON parse error on empty response, but invite still succeeds
        if (response.error) {
          // Check if it's just a JSON parse error (invite likely succeeded)
          if (response.error.message?.includes('JSON') || response.error.message?.includes('Unexpected end')) {
            // Likely a successful invite with empty response body
            successCount++;
          } else {
            failCount++;
            errors.push(`${parent.email}: ${response.error.message}`);
          }
        } else {
          successCount++;
        }
      } catch (err) {
        // JSON parse errors on empty successful responses
        if (err.message?.includes('JSON') || err.message?.includes('Unexpected end')) {
          successCount++;
        } else {
          failCount++;
          errors.push(`${parent.email}: ${err.message}`);
        }
      }

      // Small delay to avoid rate limiting
      if (i < selectedParents.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    setLoading(false);
    setAllFilteredData(null);

    // Show result notification
    if (failCount === 0) {
      notify(`${successCount} ouer(s) suksesvol genooi`, { type: 'success' });
    } else if (successCount === 0) {
      notify(`Alle uitnodigings het misluk. ${errors[0]}`, { type: 'error' });
    } else {
      notify(`${successCount} genooi, ${failCount} misluk`, { type: 'warning' });
    }

    // Refresh the list and clear selection
    unselectAll();
    refresh();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {selectedIds.length} geselekteer
      </Typography>

      {/* Show "Select All X" button when all on page are selected but there are more */}
      {allOnPageSelected && hasMoreRecords && !allRecordsSelected && (
        <Button
          label={selectingAll ? 'Laai...' : `Selekteer alle ${total} rekords`}
          onClick={handleSelectAll}
          disabled={loading || selectingAll}
        >
          {selectingAll ? <CircularProgress size={18} /> : <SelectAllIcon />}
        </Button>
      )}

      <Button
        label={loading ? `Nooi uit... (${progress.current}/${progress.total})` : 'Nooi Geselekteerdes Uit'}
        onClick={handleBulkInvite}
        disabled={loading || selectedIds.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <SendIcon />}
      </Button>
    </Box>
  );
};

export default BulkInviteButton;
